// /src/components/home/ProjectRail.js
//
// Right-hand rail: empty state, or detail with optional secondary (hover) image.

import React from "react";
import { useNavigate } from "react-router-dom";
import {
  railEmptyStateClass,
  railMetaRowClass,
  railProjectTitleClass,
  railDescriptionClass,
  railDetailsListClass,
  railDetailLabelClass,
  railDetailValueClass,
  railOpenButtonClass,
} from "../../tokens";

const ProjectRail = ({ project, index, total }) => {
  const navigate = useNavigate();

  if (!project) {
    return (
      <aside
        className="
          h-full w-full
          flex items-center justify-center
          text-content-muted
        "
        aria-live="polite"
      >
        <p className={railEmptyStateClass}>
          Hover a project
        </p>
      </aside>
    );
  }

  const positionLabel = `${String(index + 1).padStart(2, "0")} / ${String(total).padStart(2, "0")}`;
  const toolsLabel = Array.isArray(project.tools)
    ? project.tools.join(" · ")
    : project.tools || "—";

  const secondarySrc = project.hoverMediaSrc;
  const secondaryType = project.hoverMediaType || "image";
  const showSecondary =
    secondarySrc &&
    (secondaryType === "image" || secondaryType === "gif");

  return (
    <aside
      className="h-full w-full flex flex-col"
      aria-live="polite"
      aria-atomic="true"
    >
      <div className={railMetaRowClass}>
        <span>{positionLabel}</span>
        <span>{project.year ?? ""}</span>
      </div>

      <h2 className={railProjectTitleClass}>
        {project.title}
        {project.subtitle && (
          <span className="text-content-secondary dark:text-neutral-400">
            {", "}
            {project.subtitle}
          </span>
        )}
      </h2>

      {project.description && (
        <p className={railDescriptionClass}>
          {project.description}
        </p>
      )}

      {showSecondary && (
        <div className="mt-6 w-full max-w-[280px]">
          <div className="relative aspect-square w-full overflow-hidden bg-neutral-100 dark:bg-neutral-900 outline outline-1 outline-neutral-200 dark:outline-neutral-800 outline-offset-[-1px]">
            {secondaryType === "gif" ? (
              <img
                src={secondarySrc}
                alt=""
                className={`absolute inset-0 m-auto h-full w-full max-h-full max-w-full object-contain object-center ${project.invert ? "dark:invert" : ""}`}
              />
            ) : (
              <img
                src={secondarySrc}
                alt=""
                loading="lazy"
                decoding="async"
                className={`absolute inset-0 m-auto h-full w-full max-h-full max-w-full object-contain object-center ${project.invert ? "dark:invert" : ""}`}
              />
            )}
          </div>
        </div>
      )}

      <dl className={railDetailsListClass}>
        <div>
          <dt className={railDetailLabelClass}>Role</dt>
          <dd className={railDetailValueClass}>{project.role || "—"}</dd>
        </div>
        <div>
          <dt className={railDetailLabelClass}>Tools</dt>
          <dd className={railDetailValueClass}>{toolsLabel}</dd>
        </div>
      </dl>

      <button
        type="button"
        onClick={() => project.targetUrl && navigate(project.targetUrl)}
        data-cursor="pointer"
        className={railOpenButtonClass}
      >
        <span>Open</span>
        <span aria-hidden="true">&rarr;</span>
      </button>
    </aside>
  );
};

export default ProjectRail;
